import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const emptyForm = {
  title: '',
  category: 'teknoloji',
  description: '',
  impactLevel: 'medium',
  tags: ''
};

const statusLabels = {
  submitted: 'Yeni',
  reviewing: 'Değerlendiriliyor',
  planned: 'Planlandı',
  implemented: 'Uygulandı',
  declined: 'Kapatılan'
};

const categoryLabels = {
  teknoloji: 'Teknoloji',
  surec: 'Süreç',
  verimlilik: 'Verimlilik',
  guvenlik: 'Güvenlik',
  surdurulebilirlik: 'Sürdürülebilirlik',
  ik: 'İnsan Kaynakları',
  diger: 'Diğer'
};

const impactLabels = {
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek'
};

const InnovationPage = () => {
  const [innovations, setInnovations] = useState([]);
  const [stats, setStats] = useState({ total: 0, submitted: 0, reviewing: 0, implemented: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchInnovations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getInnovations();
      setInnovations(response.innovations || []);
      setStats(response.stats || { total: 0, submitted: 0, reviewing: 0, implemented: 0 });
    } catch (fetchError) {
      console.error('Innovation page error:', fetchError);
      setError('Öneri verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInnovations();
  }, []);

  const filteredInnovations = useMemo(() => {
    if (activeTab === 'all') {
      return innovations;
    }

    if (activeTab === 'popular') {
      return [...innovations].sort((left, right) => right.voteCount - left.voteCount);
    }

    if (activeTab === 'implemented') {
      return innovations.filter((innovation) => innovation.status === 'implemented');
    }

    return innovations.filter((innovation) => innovation.status === activeTab);
  }, [activeTab, innovations]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      await api.createInnovation({
        title: formData.title,
        category: formData.category,
        description: formData.description,
        impactLevel: formData.impactLevel,
        tags: formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      });

      setFormData(emptyForm);
      setShowSubmitForm(false);
      await fetchInnovations();
    } catch (submitError) {
      console.error('Create innovation error:', submitError);
      setError('Öneri kaydı yapılamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (innovationId) => {
    try {
      await api.toggleInnovationVote(innovationId);
      await fetchInnovations();
    } catch (voteError) {
      console.error('Vote innovation error:', voteError);
      setError('Oy işlemi tamamlananamadı.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <h3 className="mb-2">Öneri ve Talep Kutusu</h3>
                <p className="text-muted mb-0">
                  Öneri ve talepleri toplayan, oylayan ve durum takibi yapan kurumsal platform.
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowSubmitForm(true)}>
                Yeni Öneri Gönder
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        {[
          ['Toplam Öneri', stats.total],
          ['Yeni Gelen', stats.submitted],
          ['Değerlendirilen', stats.reviewing],
          ['Uygulandı', stats.implemented]
        ].map(([label, value]) => (
          <div key={label} className="col-md-3 col-sm-6 mb-3">
            <div className="box h-100">
              <div className="box-body">
                <div className="text-muted small">{label}</div>
                <div className="h3 mb-0">{value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showSubmitForm && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="box">
              <div className="box-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Öneri Formu</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSubmitForm(false)}>
                  Kapat
                </button>
              </div>
              <div className="box-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Başlık</label>
                      <input
                        className="form-control"
                        value={formData.title}
                        onChange={(event) =>
                          setFormData((current) => ({ ...current, title: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Kategori</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(event) =>
                          setFormData((current) => ({ ...current, category: event.target.value }))
                        }
                      >
                        {Object.entries(categoryLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Beklenen Etki</label>
                      <select
                        className="form-select"
                        value={formData.impactLevel}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            impactLevel: event.target.value
                          }))
                        }
                      >
                        {Object.entries(impactLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Açıklama</label>
                      <textarea
                        className="form-control"
                        rows="5"
                        value={formData.description}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            description: event.target.value
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Etiketler</label>
                      <input
                        className="form-control"
                        placeholder="örnek: saha, otomasyon, maliyet"
                        value={formData.tags}
                        onChange={(event) =>
                          setFormData((current) => ({ ...current, tags: event.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSubmitForm(false)}>
                      Vazgeç
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Kaydediliyor...' : 'Öneriyi Gönder'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-2">
            {[
              ['all', 'Tüm Öneriler'],
              ['popular', 'En Çok Oy Alanlar'],
              ['reviewing', 'Değerlendirilenler'],
              ['implemented', 'Uygulananlar']
            ].map(([value, label]) => (
              <button
                key={value}
                className={`btn ${activeTab === value ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="row">
        {filteredInnovations.map((innovation) => (
          <div key={innovation.id} className="col-lg-6 mb-4">
            <div className="box h-100">
              <div className="box-body d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <span className="badge bg-light text-dark">
                        {categoryLabels[innovation.category] || innovation.category}
                      </span>
                      <span className="badge bg-light text-dark">
                        Etki: {impactLabels[innovation.impactLevel] || innovation.impactLevel}
                      </span>
                    </div>
                    <h5 className="mb-1">{innovation.title}</h5>
                  </div>
                  <span className="badge bg-secondary">{statusLabels[innovation.status]}</span>
                </div>

                <p className="text-muted flex-grow-1">{innovation.description}</p>

                <div className="small text-muted mb-3">
                  <div>
                    {innovation.author?.firstName} {innovation.author?.lastName} -{' '}
                    {innovation.department}
                  </div>
                  <div>
                    {new Date(innovation.createdAt).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                  {innovation.feedback && <div>Geri dönüş: {innovation.feedback}</div>}
                </div>

                {innovation.tags?.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {innovation.tags.map((tag) => (
                      <span key={`${innovation.id}-${tag}`} className="badge bg-light text-dark">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-auto">
                  <button
                    className={`btn btn-sm ${innovation.hasVoted ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleVote(innovation.id)}
                  >
                    {innovation.hasVoted ? 'Oy Verdiniz' : 'Destekle'} ({innovation.voteCount})
                  </button>
                  <small className="text-muted">Durum: {statusLabels[innovation.status]}</small>
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredInnovations.length === 0 && (
          <div className="col-12">
            <div className="box">
              <div className="box-body text-center text-muted py-5">
                Bu görünüm için kayıtlı öneri bulunmuyor.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InnovationPage;
